import React, { useState } from 'react'
import { Grid, Typography, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core'

import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { CustomCheckbox } from 'components/CustomCheckbox'

import { useStyles } from './styles'

export interface TimeRestrictionConfig {
  timeRestrictionEnabled: boolean
  allowedDays: number[] // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado
  startTime: string // formato HH:MM
  endTime: string // formato HH:MM
}

interface TimeRestrictionConfigComponentProps {
  config: TimeRestrictionConfig
  onChange: (config: TimeRestrictionConfig) => void
  disabled?: boolean
  hideTimeSelectors?: boolean
}

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' },
]

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0')
  return { value: `${hour}:00`, label: `${hour}:00` }
})

// Add 23:59 option for end time
const TIME_OPTIONS_WITH_END = [...TIME_OPTIONS, { value: '23:59', label: '23:59' }]

const TimeRestrictionConfigComponent: React.FC<TimeRestrictionConfigComponentProps> = ({
  config,
  onChange,
  disabled,
  hideTimeSelectors = false,
}) => {
  const classes = useStyles()
  const [timeRestrictionEnabled, setTimeRestrictionEnabled] = useState(
    config.timeRestrictionEnabled,
  )
  const [allowedDays, setAllowedDays] = useState<number[]>(
    config.allowedDays.length > 0 ? config.allowedDays : [0, 1, 2, 3, 4, 5, 6],
  )
  const [startTime, setStartTime] = useState(config.startTime || '00:00')
  const [endTime, setEndTime] = useState(config.endTime || '23:59')

  const handleToggleRestriction = () => {
    const newEnabled = !timeRestrictionEnabled
    setTimeRestrictionEnabled(newEnabled)
    onChange({
      timeRestrictionEnabled: newEnabled,
      allowedDays: newEnabled ? allowedDays : [0, 1, 2, 3, 4, 5, 6],
      startTime: newEnabled ? startTime : '00:00',
      endTime: newEnabled ? endTime : '23:59',
    })
  }

  const handleDayToggle = (dayValue: number) => {
    const newAllowedDays = allowedDays.includes(dayValue)
      ? allowedDays.filter(day => day !== dayValue)
      : [...allowedDays, dayValue]

    setAllowedDays(newAllowedDays)
    onChange({
      timeRestrictionEnabled,
      allowedDays: newAllowedDays,
      startTime,
      endTime,
    })
  }

  const handleStartTimeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newStartTime = event.target.value as string
    setStartTime(newStartTime)
    onChange({
      timeRestrictionEnabled,
      allowedDays,
      startTime: newStartTime,
      endTime,
    })
  }

  const handleEndTimeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newEndTime = event.target.value as string
    setEndTime(newEndTime)
    onChange({
      timeRestrictionEnabled,
      allowedDays,
      startTime,
      endTime: newEndTime,
    })
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <CustomFormControlLabel
          control={
            <CustomCheckbox
              checked={timeRestrictionEnabled}
              onChange={handleToggleRestriction}
              disabled={disabled}
            />
          }
          label="Activar restricción horaria"
        />
      </Grid>

      {timeRestrictionEnabled && (
        <>
          <Grid item xs={12}>
            <Typography className={classes.dialogSubTitle}>Días de la semana</Typography>
            <Grid container spacing={1}>
              {DAYS_OF_WEEK.map(day => (
                <Grid item xs={6} sm={4} key={day.value}>
                  <CustomFormControlLabel
                    control={
                      <CustomCheckbox
                        checked={allowedDays.includes(day.value)}
                        onChange={() => handleDayToggle(day.value)}
                        disabled={disabled}
                      />
                    }
                    label={day.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {!hideTimeSelectors && (
            <Grid item xs={12}>
              <Typography className={classes.dialogSubTitle}>Horario</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Hora de inicio</InputLabel>
                    <Select value={startTime} onChange={handleStartTimeChange} disabled={disabled}>
                      {TIME_OPTIONS.map(time => (
                        <MenuItem key={time.value} value={time.value}>
                          {time.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Hora de fin</InputLabel>
                    <Select value={endTime} onChange={handleEndTimeChange} disabled={disabled}>
                      {TIME_OPTIONS_WITH_END.map(time => (
                        <MenuItem key={time.value} value={time.value}>
                          {time.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Grid>
  )
}

TimeRestrictionConfigComponent.defaultProps = {
  disabled: false,
  hideTimeSelectors: false,
}

export { TimeRestrictionConfigComponent as TimeRestrictionConfig }
